/**
 * Hero Cycles – Service Worker
 * Domain: https://www.herocycles.com/
 * Version: 1.0.0
 */

const SW_VERSION     = 'v1.0.0';
const STATIC_CACHE   = `hero-cycles-static-${SW_VERSION}`;
const DYNAMIC_CACHE  = `hero-cycles-dynamic-${SW_VERSION}`;
const IMAGE_CACHE    = `hero-cycles-images-${SW_VERSION}`;

// ─── Pages to pre-cache on install ────────────────────────────────────────────
const PRECACHE_URLS = [
  '/',
  '/collections/all',
  '/cart',
  '/pages/about-us',
];

// ─── Install ──────────────────────────────────────────────────────────────────
self.addEventListener('install', (event) => {
  console.log('[Hero SW] Installing…', SW_VERSION);
  event.waitUntil(
    caches.open(STATIC_CACHE)
      .then((cache) => cache.addAll(PRECACHE_URLS))
      .then(() => self.skipWaiting())
  );
});

// ─── Activate – purge stale caches ────────────────────────────────────────────
self.addEventListener('activate', (event) => {
  console.log('[Hero SW] Activating…', SW_VERSION);
  const allowedCaches = [STATIC_CACHE, DYNAMIC_CACHE, IMAGE_CACHE];
  event.waitUntil(
    caches.keys().then((keys) =>
      Promise.all(
        keys
          .filter((key) => !allowedCaches.includes(key))
          .map((key) => {
            console.log('[Hero SW] Deleting old cache:', key);
            return caches.delete(key);
          })
      )
    ).then(() => self.clients.claim())
  );
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isStaticAsset(url) {
  return /\.(js|css|woff2?|ttf|eot|otf)(\?.*)?$/.test(url.pathname);
}

function isImage(url) {
  return /\.(png|jpe?g|gif|webp|svg|ico|avif)(\?.*)?$/.test(url.pathname);
}

function isShopifyInternal(url) {
  // Skip Shopify analytics, checkout, admin, cart mutations, CDN auth
  return (
    url.hostname.includes('shopify.com') ||
    url.hostname.includes('shopifycloud.com') ||
    url.pathname.startsWith('/admin') ||
    url.pathname.startsWith('/checkout') ||
    url.pathname.startsWith('/payments') ||
    url.pathname === '/cart/add' ||
    url.pathname === '/cart/change' ||
    url.pathname === '/cart/update' ||
    url.pathname === '/cart/clear'
  );
}

function isCDN(url) {
  // Shopify CDN – cache images aggressively
  return url.hostname.includes('cdn.shopify.com');
}

// ─── Fetch ────────────────────────────────────────────────────────────────────
self.addEventListener('fetch', (event) => {
  const { request } = event;
  const url = new URL(request.url);

  // Only handle GET
  if (request.method !== 'GET') return;

  // Let Shopify internals go straight to network
  if (isShopifyInternal(url)) return;

  // ── Strategy 1: Cache-First for static assets (JS/CSS/fonts) ──────────────
  if (isStaticAsset(url)) {
    event.respondWith(
      caches.match(request).then((cached) => {
        if (cached) return cached;
        return fetch(request).then((response) => {
          if (response.ok) {
            const clone = response.clone();
            caches.open(STATIC_CACHE).then((cache) => cache.put(request, clone));
          }
          return response;
        });
      })
    );
    return;
  }

  // ── Strategy 2: Cache-First for CDN images (long TTL) ─────────────────────
  if (isCDN(url) || isImage(url)) {
    event.respondWith(
      caches.match(request).then((cached) => {
        if (cached) return cached;
        return fetch(request).then((response) => {
          if (response.ok) {
            const clone = response.clone();
            caches.open(IMAGE_CACHE).then((cache) => cache.put(request, clone));
          }
          return response;
        }).catch(() => {
          // Return a transparent placeholder if offline
          return new Response('', { status: 408, statusText: 'Offline' });
        });
      })
    );
    return;
  }

  // ── Strategy 3: Network-First for HTML pages ───────────────────────────────
  event.respondWith(
    fetch(request)
      .then((response) => {
        if (response.ok) {
          const clone = response.clone();
          caches.open(DYNAMIC_CACHE).then((cache) => cache.put(request, clone));
        }
        return response;
      })
      .catch(() => {
        // Serve cached page if available
        return caches.match(request).then((cached) => {
          if (cached) return cached;
          // Fallback to cached homepage
          return caches.match('/');
        });
      })
  );
});

// ─── Push Notifications ───────────────────────────────────────────────────────
self.addEventListener('push', (event) => {
  let data = { title: 'Hero Cycles', body: 'Check out our latest offers!' };
  try { data = event.data?.json() ?? data; } catch (_) {}

  event.waitUntil(
    self.registration.showNotification(data.title, {
      body:    data.body,
      icon:    'https://cdn.shopify.com/s/files/1/0971/8775/8386/files/Hero-favicon1.png?v=1774609240',
      badge:   'https://cdn.shopify.com/s/files/1/0971/8775/8386/files/Hero-favicon1.png?v=1774609240',
      data:    { url: data.url || 'https://www.herocycles.com/' },
      actions: [
        { action: 'shop', title: '🛒 Shop Now' },
        { action: 'dismiss', title: '✕ Dismiss'  }
      ],
      requireInteraction: false,
    })
  );
});

// ─── Notification Click ───────────────────────────────────────────────────────
self.addEventListener('notificationclick', (event) => {
  event.notification.close();
  if (event.action === 'dismiss') return;

  const targetUrl = event.notification.data?.url || 'https://www.herocycles.com/';
  event.waitUntil(
    clients.matchAll({ type: 'window', includeUncontrolled: true }).then((windowClients) => {
      const existing = windowClients.find((c) => c.url === targetUrl && 'focus' in c);
      if (existing) return existing.focus();
      return clients.openWindow(targetUrl);
    })
  );
});

// ─── Background Sync (cart recovery) ─────────────────────────────────────────
self.addEventListener('sync', (event) => {
  if (event.tag === 'sync-cart') {
    event.waitUntil(syncCart());
  }
});

async function syncCart() {
  console.log('[Hero SW] Background sync: cart');
  // Add any offline cart sync logic here
}
